import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useCanViewCari, useCanViewPurchasing } from '@app/features/auth/api'
import { cn } from '@app/lib/cn'

/**
 * Cross-links between the six modules.
 *
 * Korgun's detail forms are dead ends: a purchase order prints the supplier's
 * code and you re-open the cari module by hand to see who that is. Every code
 * printed on a detail screen here is the way into the record behind it, and
 * they all look the same — kordovan text, underline on hover. The row links on
 * the LIST screens stay ink-coloured (the whole row is the target there, so an
 * accent on every first cell would be noise); an inline reference in the middle
 * of a sentence or a dense table has to announce itself.
 *
 * Each helper takes the same shape: the identifier, optional children to print
 * instead of it, and an optional title for the tooltip. Keeping the six of them
 * here means a route rename is one edit, not a grep across ten screens.
 *
 * Two of them lead into guarded modules (`CariLink` → cari, `PurchaseLink` →
 * satınalma; see worker/api/auth.contract.ts), and they are asked SEPARATELY:
 * a `purchasing` account follows the purchase order and reads the cari code as
 * text, a `staff` account reads both as text. The reference still says who the
 * row belongs to on a stok / üretim / sipariş screen, it just is not a door any
 * more. Offering the link would walk them into the route guard and drop them
 * back on the panel with no explanation — and this is the one file that knows
 * about every one of those links.
 *
 * A door whose whole content is the promise to open the module — the "Cari
 * kartını aç" button on a purchase order, the external-link icon on a supplier
 * tile — has no text to degrade to, so it is left out instead: `CariOnly`
 * below. Both shapes are decided here, by the same capability.
 */

const entityLinkClass = 'text-kordovan underline-offset-2 transition-colors hover:underline'

interface EntityLinkProps {
  children?: ReactNode
  /** Tooltip — say what opens, e.g. "Stok kartını aç". */
  title?: string
  className?: string
}

/**
 * Only the two guarded links take this: what to wear when the reference is
 * printed as plain text instead of a link. `className` is written for an anchor
 * and callers put hover states in it (`hover:text-kordovan`), which a `<span>`
 * honours just as happily — a name that lights up under the cursor and then
 * does nothing is exactly the dead link this branch exists to avoid. Formatting
 * that is about the value rather than the affordance (`font-mono`) can stay in
 * `className`: without a `plainClassName` the plain branch keeps it.
 */
interface DegradableLinkProps extends EntityLinkProps {
  plainClassName?: string
}

/** Stock card — `StokKart.SKod`. Codes carry spaces and dots; the router encodes them. */
export function StockLink({ skod, children, title, className }: EntityLinkProps & { skod: string }) {
  return (
    <Link
      to="/stok/$skod"
      params={{ skod }}
      title={title ?? `${skod} stok kartını aç`}
      className={cn(entityLinkClass, className)}
    >
      {children ?? skod}
    </Link>
  )
}

/** Model card — `Model_M.ModelKod`. */
export function ModelLink({
  modelKod,
  children,
  title,
  className,
}: EntityLinkProps & { modelKod: string }) {
  return (
    <Link
      to="/modeller/$modelKod"
      params={{ modelKod }}
      title={title ?? `${modelKod} model kartını aç`}
      className={cn(entityLinkClass, className)}
    >
      {children ?? modelKod}
    </Link>
  )
}

/**
 * Sales order — `Siparis_Kay.SipNo` with `SipTip` 'S'/'ST'.
 *
 * A purchase order (`SipTip` 'A') has its own canonical screen; use
 * `PurchaseLink` for those. `/siparisler/<purchase order>` redirects there
 * anyway, but linking straight at the right screen skips the bounce.
 */
export function OrderLink({ sipNo, children, title, className }: EntityLinkProps & { sipNo: number }) {
  return (
    <Link
      to="/siparisler/$sipNo"
      params={{ sipNo: String(sipNo) }}
      title={title ?? `${sipNo} numaralı siparişi aç`}
      className={cn(entityLinkClass, className)}
    >
      {children ?? sipNo}
    </Link>
  )
}

/** Purchase order — `Siparis_Kay.SipNo` with `SipTip = 'A'`. Guarded module. */
export function PurchaseLink({
  sipNo,
  children,
  title,
  className,
  plainClassName,
}: DegradableLinkProps & { sipNo: number }) {
  const canOpen = useCanViewPurchasing()
  // Plain text, without the kordovan accent — that colour promises a target.
  if (!canOpen) return <span className={plainClassName ?? className}>{children ?? sipNo}</span>

  return (
    <Link
      to="/satinalma/$sipNo"
      params={{ sipNo: String(sipNo) }}
      title={title ?? `${sipNo} numaralı alış siparişini aç`}
      className={cn(entityLinkClass, className)}
    >
      {children ?? sipNo}
    </Link>
  )
}

/** Cari account — `Cari_Kart.CKod`, customer and supplier alike. Admin only. */
export function CariLink({
  ckod,
  children,
  title,
  className,
  plainClassName,
}: DegradableLinkProps & { ckod: string }) {
  const canOpen = useCanViewCari()
  if (!canOpen) return <span className={plainClassName ?? className}>{children ?? ckod}</span>

  return (
    <Link
      to="/cari/$ckod"
      params={{ ckod }}
      title={title ?? `${ckod} cari kartını aç`}
      className={cn(entityLinkClass, className)}
    >
      {children ?? ckod}
    </Link>
  )
}

/**
 * Renders a cari door only for an account that can walk through it.
 *
 * `CariLink` degrades because the reference it prints is also a VALUE — a
 * supplier name, an account code — worth reading with no link under it. A
 * button labelled "Cari kartını aç" and an external-link icon carry nothing but
 * the affordance, so there is no plain form of them: an account without the
 * module gets neither, rather than a control that lies or a link that bounces
 * off the route guard.
 *
 * Only the satınalma screens need this today — they are the ones a role without
 * cari (`purchasing`) can open while their records still point at cari cards.
 */
export function CariOnly({ children }: { children: ReactNode }) {
  return useCanViewCari() ? <>{children}</> : null
}

/** Production order — `Urtx_Emir.EmirNo`. */
export function EmirLink({ emirNo, children, title, className }: EntityLinkProps & { emirNo: number }) {
  return (
    <Link
      to="/uretim/$emirNo"
      params={{ emirNo: String(emirNo) }}
      title={title ?? `${emirNo} numaralı üretim emrini aç`}
      className={cn(entityLinkClass, className)}
    >
      {children ?? emirNo}
    </Link>
  )
}
